/**
 * PATH: packages/ui/src/qa/QaPhotoLightbox.tsx
 * FILE_ID: FE-QA-PHOTO-LIGHTBOX-001
 * PURPOSE: Whole-photo QA viewer with zoom, pan, and prev/next.
 * LANDMINE: Copy the email lightbox SHELL only. Do not import email-hub
 *      (compose stores, toasts, router). Zoom rule: contain at <=1, free at >1.
 */

import { ChevronLeft, ChevronRight, Download, ExternalLink, Minus, Plus, RotateCcw, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { clampSize } from '../components/panelResize'
import type { QaGalleryPhoto } from './QaPhotoGallery'

export interface QaPhotoLightboxProps {
  photos: QaGalleryPhoto[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

export function QaPhotoLightbox({
  photos,
  index,
  onIndexChange,
  onClose,
}: QaPhotoLightboxProps) {
  const photo = photos[index]
  const [zoom, setZoom] = useState(1)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; sl: number; st: number } | null>(null)

  useEffect(() => {
    setZoom(1)
  }, [index])

  useEffect(() => {
    const prior = document.activeElement as HTMLElement | null
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight' && index < photos.length - 1) onIndexChange(index + 1)
      if (event.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1)
    }
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      setZoom((prev) => clampSize(prev * (event.deltaY < 0 ? 1.25 : 0.8), 0.5, 4))
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('wheel', onWheel)
      prior?.focus?.()
    }
  }, [index, onClose, onIndexChange, photos.length])

  if (!photo || typeof document === 'undefined') return null

  const src = photo.src
  const canStep = photos.length > 1

  // Reused from: packages/ui/src/mobile/MobileSheet.tsx (createPortal)
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="QA photo"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-[95vw] flex-col items-center gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-center gap-1.5 rounded-full border border-border bg-background/95 px-2 py-1.5 shadow-lg">
          <button type="button" onClick={() => setZoom((z) => clampSize(z * 1.25, 0.5, 4))} className="rounded-full px-2 py-1 text-xs" title="Zoom in">
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => setZoom((z) => clampSize(z / 1.25, 0.5, 4))} className="rounded-full px-2 py-1 text-xs" title="Zoom out">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => setZoom(1)} className="rounded-full px-2 py-1 text-xs" title="Reset zoom">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          {src ? (
            <>
              <a href={src} download className="rounded-full px-2 py-1 text-xs" title="Download">
                <Download className="h-3.5 w-3.5" />
              </a>
              <a href={src} target="_blank" rel="noreferrer" className="rounded-full px-2 py-1 text-xs" title="Open in tab">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </>
          ) : null}
          {canStep ? (
            <span className="px-2 text-[11px] text-muted-foreground">
              {index + 1} of {photos.length}
            </span>
          ) : null}
          <button type="button" onClick={onClose} className="rounded-full px-2 py-1 text-xs text-destructive" title="Close">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div
          ref={scrollerRef}
          className={`flex min-h-0 w-full flex-1 ${zoom <= 1 ? 'items-center justify-center overflow-hidden' : 'items-start justify-start overflow-auto'}`}
          onPointerDown={(event) => {
            if (zoom <= 1) return
            const el = scrollerRef.current
            if (!el) return
            drag.current = { x: event.clientX, y: event.clientY, sl: el.scrollLeft, st: el.scrollTop }
            ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
          }}
          onPointerMove={(event) => {
            if (!drag.current || !scrollerRef.current) return
            scrollerRef.current.scrollLeft = drag.current.sl - (event.clientX - drag.current.x)
            scrollerRef.current.scrollTop = drag.current.st - (event.clientY - drag.current.y)
          }}
          onPointerUp={() => {
            drag.current = null
          }}
        >
          {src ? (
            <img
              src={src}
              alt={photo.alt || 'QA photo'}
              draggable={false}
              style={zoom <= 1 ? undefined : { width: `${zoom * 100}%` }}
              className={zoom <= 1 ? 'max-h-[70vh] max-w-full object-contain' : 'max-w-none'}
            />
          ) : (
            <p className="rounded-lg bg-background px-4 py-6 text-sm text-muted-foreground">Photo unavailable</p>
          )}
        </div>
        {canStep ? (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => onIndexChange(index - 1)}
              className="rounded-full border border-border bg-background p-2 disabled:opacity-40"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={index === photos.length - 1}
              onClick={() => onIndexChange(index + 1)}
              className="rounded-full border border-border bg-background p-2 disabled:opacity-40"
              aria-label="Next photo"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
        {photo.caption ? <p className="text-center text-sm text-white">{photo.caption}</p> : null}
      </div>
    </div>,
    document.body,
  )
}
